import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Fade,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock,
  Person,
  Key,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';

export default function LoginPage() {
  const { login } = useAuth();
  const [step, setStep] = useState('credentials'); // 'credentials' | '2fa'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [tempToken, setTempToken] = useState(null);
  const [rememberBrowser, setRememberBrowser] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login({ username, password });
      if (result.requires_2fa) {
        setTempToken(result.temp_token);
        setStep('2fa');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login({
        otp_code: otpCode,
        temp_token: tempToken,
        remember_browser: rememberBrowser,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Codice OTP non valido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 30%, #1E88E5 60%, #42A5F5 100%)',
      }}
    >
      {/* Decorative circles */}
      <Box
        sx={{
          position: 'absolute',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
          top: -200,
          right: -150,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.04)',
          bottom: -100,
          left: -100,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <Fade in timeout={600}>
        <Paper
          elevation={24}
          sx={{
            p: 5,
            width: '100%',
            maxWidth: 420,
            mx: 2,
            borderRadius: 3,
            position: 'relative',
            zIndex: 1,
            backdropFilter: 'blur(10px)',
            bgcolor: 'rgba(255,255,255,0.97)',
          }}
        >
          {/* Logo / Brand */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
              src="/logo-minerva.png"
              alt="Minerva Medica"
              sx={{
                height: 80,
                width: 'auto',
                mx: 'auto',
                mb: 2,
                display: 'block',
              }}
            />
            <Typography variant="h5" fontWeight={700} color="text.primary">
              File Manager QR
            </Typography>
          </Box>

          {step === 'credentials' ? (
            <form onSubmit={handleLoginSubmit}>
              <TextField
                fullWidth
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                required
                disabled={loading}
                sx={{ mb: 2.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !username || !password}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #1565C0, #1E88E5)',
                  boxShadow: '0 4px 14px rgba(21,101,192,0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0D47A1, #1565C0)',
                    boxShadow: '0 6px 20px rgba(21,101,192,0.5)',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Accedi'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: 'primary.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1.5,
                  }}
                >
                  <Key color="primary" />
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  Verifica a due fattori
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Inserisci il codice dalla tua app di autenticazione
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Codice OTP"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoFocus
                required
                disabled={loading}
                sx={{ mb: 2 }}
                inputProps={{
                  maxLength: 6,
                  style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5em' },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Key color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberBrowser}
                    onChange={(e) => setRememberBrowser(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Ricorda questo browser per 60 giorni
                  </Typography>
                }
                sx={{ mb: 2 }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || otpCode.length < 6}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #1565C0, #1E88E5)',
                  boxShadow: '0 4px 14px rgba(21,101,192,0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0D47A1, #1565C0)',
                    boxShadow: '0 6px 20px rgba(21,101,192,0.5)',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verifica'}
              </Button>

              <Button
                fullWidth
                variant="text"
                onClick={() => {
                  setStep('credentials');
                  setOtpCode('');
                  setTempToken(null);
                  setError('');
                }}
                sx={{ mt: 1.5 }}
                disabled={loading}
              >
                Torna al login
              </Button>
            </form>
          )}
        </Paper>
      </Fade>
    </Box>
  );
}
